// Registration-window client mirror (§11 / §G) — keep in LOCKSTEP with
// backend helpers/oireachtasDeadlines.js (same semantics, same states).
// The server is authoritative: quote responses carry `deadlines` computed
// there; this mirror covers UI that renders before a quote exists (T1 banner,
// wizard load). changeFreezeDate is deliberately not consulted (reserved for
// the NAIDC paid-change workflow; ECRO's final close = lateEntryCloseDate).
//
// Close dates are CALENDAR DAYS (stored as midnight UTC, shown as that day)
// that end at 11:59:59.999 pm in the EVENT's time zone — `event.timeZone`,
// which GET /oireachtas/:id always returns (derived from the venue). Same
// math as backend helpers/oireachtasTimeZone.js endOfCalendarDay.

const DAY_MS = 24 * 60 * 60 * 1000;

// The event's zone, or UTC when absent — or unknown to THIS browser's ICU
// (an older engine vs. a newer IANA id): Intl would throw during render and
// blank the page, so fall back rather than crash. The server stays the gate.
const zoneOf = (event) => {
  const tz = event && event.timeZone;
  if (!tz) return "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch (e) {
    return "UTC";
  }
};

// Offset of timeZone from UTC at instant ms, in ms (local = utc + offset).
function zoneOffsetMs(timeZone, ms) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(ms));
  const v = Object.fromEntries(parts.map((p) => [p.type, Number(p.value)]));
  const asUtc = Date.UTC(v.year, v.month - 1, v.day, v.hour, v.minute, v.second);
  return asUtc - (ms - (ms % 1000));
}

/** 23:59:59.999 on the stored date's calendar day in timeZone (DST-safe). */
export function endOfCalendarDay(date, timeZone = "UTC") {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const wall = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999);
  let instant = wall - zoneOffsetMs(timeZone, wall);
  instant = wall - zoneOffsetMs(timeZone, instant);
  return new Date(instant);
}

/**
 * @returns {{ state: "open"|"late"|"closed",
 *             entriesCloseDate: Date|null, lateEntryCloseDate: Date|null,
 *             hardClose: Date|null, daysLeft: number|null }}
 *          The dates are the INSTANTS each deadline ends. daysLeft counts
 *          whole days after today to the hard close — 0 on the last day,
 *          because the close instant is the END of that day.
 */
export function regWindow(event = {}, now = new Date()) {
  const tz = zoneOf(event);
  const close = endOfCalendarDay(event.entriesCloseDate, tz);
  const late = endOfCalendarDay(event.lateEntryCloseDate, tz);
  const hardClose = late || close;
  const t = now.getTime();

  let state = "open";
  if (hardClose && t > hardClose.getTime()) {
    state = "closed";
  } else if (close && late && t > close.getTime() && t <= late.getTime()) {
    state = "late";
  }

  const daysLeft =
    state === "closed" || !hardClose
      ? null
      : Math.max(0, Math.ceil((hardClose.getTime() - t) / DAY_MS) - 1);

  return { state, entriesCloseDate: close, lateEntryCloseDate: late, hardClose, daysLeft };
}

/**
 * "11:59 pm EDT" — when a close day ends, in the event's zone (for banners;
 * teachers in other zones see the venue's time, which is what applies).
 */
export function closeTimeLabel(event, date) {
  const end = endOfCalendarDay(date, zoneOf(event));
  if (!end) return null;
  const zone = new Intl.DateTimeFormat("en-US", { timeZone: zoneOf(event), timeZoneName: "short" })
    .formatToParts(end)
    .find((p) => p.type === "timeZoneName");
  return `11:59 pm${zone ? ` ${zone.value}` : ""}`;
}

/** Server-echo: prefer the quote's authoritative state once one is loaded. */
export function regWindowState(event, quote) {
  if (quote && quote.deadlines && quote.deadlines.state) return quote.deadlines.state;
  return regWindow(event).state;
}

/** "+25% surcharge" / "+$100 late fee" chip text from event.fees.lateFee. */
export function lateFeeTag(event) {
  const lf = event && event.fees && event.fees.lateFee;
  if (!lf || !lf.value) return null;
  if (lf.mode === "percent") return `+${lf.value}% surcharge`;
  if (lf.mode === "flat") {
    const money = new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: (event && event.currency) || "CAD",
      maximumFractionDigits: 0,
    }).format(lf.value);
    return `+${money} late fee`;
  }
  return null;
}
